import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
  useNavigate,
  Navigate,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { jwtDecode } from "jwt-decode";
import "react-toastify/dist/ReactToastify.css";
import { HelmetProvider } from "react-helmet-async";

// Import Pages
import OnboardingPage from "./pages/OnboardingPage.tsx";
import ViewOrganizationProfilePage from "./pages/ViewOrganizationProfilePage.tsx";
import EditOrganizationProfilePage from "./pages/EditOrganizationProfilePage.tsx";
import PresentationViewPage from "./pages/PresentationViewPage.tsx";
import AuthPage from "./pages/AuthPage.tsx";
import HistoryPage from "./pages/HistoryPage.tsx";
import PresentationTypePage from "./pages/PresentationType.tsx";
import LandingPage from "./pages/LandingPage.tsx";
import PresentationShare from "./pages/PresentationShare.tsx";
import PitchDeckShare from "./components/pitchZynthShare/Share.jsx";
import PricingPage from "./pages/PricingPage.tsx";
import BlogPage from "./pages/BlogPage.tsx";
import ContactUsPage from "./pages/ContactUsPage.tsx";
import ReferPage from "./pages/ReferPage.tsx";

// Import Components
import ProtectedRoutes from "./components/shared/ProtectedRoutes.tsx";
import { SocketProvider } from "./components/payment/SubscriptionSocket.tsx";

// Import Context API
import { OutlineProvider } from "./components/presentation-view/OutlineContext.tsx";

// Import Google Analytics
import ReactGA from "react-ga";
import NewLandingPage from "./pages/NewLandingPage.tsx";
import UseCasesSalesPage from "./pages/UseCasesSalesPage.tsx";
import UseCasesProductPage from "./pages/UseCasesProductPage.tsx";
import UseCasesPitchPage from "./pages/UseCasesPitchPage.tsx";
import UseCasesMarketingPage from "./pages/UseCasesMarketingPage.tsx";
import UseCasesEmployeePage from "./pages/UseCasesEmloyeePage.tsx";
import UseCasesProjectPage from "./pages/UseCasesProjectPage.tsx";
import UseCasesBoardPage from "./pages/UseCasesBoardPage.tsx";
import UseCasesEducationPage from "./pages/UseCasesEducationPage.tsx";
import Blog1 from "./components/blogs/Blog1.tsx";
import Blog2 from "./components/blogs/Blog2.tsx";
import Blog3 from "./components/blogs/Blog3.tsx";
import Blog4 from "./components/blogs/Blog4.tsx";
import Blog5 from "./components/blogs/Blog5.tsx";
import Blog7 from "./components/blogs/Blog7.tsx";
import Blog6 from "./components/blogs/Blog6.tsx";
import Blog8 from "./components/blogs/Blog8.tsx";
import Blog9 from "./components/blogs/Blog9.tsx";
import Blog10 from "./components/blogs/Blog10.tsx";
import Homepage from "./components/orcus-landing/Homepage.tsx";
import AboutUs from "./components/orcus-landing/About.tsx";
import Login from "./components/auth/Login.tsx";
import Startup from "./components/orcus-landing/Startup.tsx";
import StartupStudio from "./components/orcus-landing/StartupStudio.tsx";
import ContactUs from "./components/orcus-landing/ContactUs.tsx";
import TermsAndConditions from "./components/orcus-landing/TermsAndConditions.tsx";
import PrivacyPolicy from "./components/orcus-landing/PrivacyPolicy.tsx";
import InvestorDashboard from "./pages/InvestorDashboard.tsx";
import './index.css';
import RefundAndCancellation from "./components/orcus-landing/refundAndCancellation.tsx";
import MontyPaySuccessPage from "./pages/MontyPaySuccessPage.tsx";
import MontyPayCancelPage from "./pages/MontyPayCancelPage.tsx";
// Extend the Window interface to include dataLayer
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

// Initialize Google Analytics
ReactGA.initialize("G-YHL4Z27NY0");

/**
 * Tracks Page Views for Google Analytics
 */
const TrackPageView: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    ReactGA.pageview(location.pathname + location.search);
  }, [location]);

  return null;
};

/**
 * ✅ Auth Token Expiry Check (Inside Router)
 * ✅ Excludes PresentationShare from auto logout
 * ✅ Fixes infinite redirect issue
 */
const AuthCheckComponent = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.pathname === "/presentation-share") {
      return;
    }

    const checkTokenExpiry = () => {
      const token = sessionStorage.getItem("authToken");
      if (!token) {
        handleLogout();
        return;
      }

      try {
        const decoded: { exp?: number } = jwtDecode(token);
        const currentTime = Math.floor(Date.now() / 1000);

        if (!decoded.exp || decoded.exp <= currentTime) {
          handleLogout();
          return;
        }

        // Set auto logout timeout
        const timeoutDuration = (decoded.exp - currentTime) * 1000;
        setTimeout(() => {
          handleLogout();
        }, timeoutDuration);
      } catch (error) {
        console.error("Invalid token:", error);
        handleLogout();
      }
    };

    const handleLogout = () => {
      sessionStorage.removeItem('authToken');
      const excludedRoutes = [
        '/share',
        '/presentation-share',
        '/pricing',
        '/',
        '/auth',
        '/white-label',
        '/#how-it-works',
        '/use-cases-sales',
        '/use-cases-product',
        '/use-cases-pitch',
        '/use-cases-marketing',
        '/use-cases-employee',
        '/use-cases-project',
        '/use-cases-board',
        '/use-cases-education',
        '/blog',
        '/contact-us',
        '/startup-studio',
        '/about',
        '/startup-accelerator',
        '/blog1',
        '/blog2',
        '/blog3',
        '/blog4',
        '/blog5',
        '/blog6',
        '/blog7',
        '/blog8',
        '/blog9',
        '/blog10',
        '/startup',
        '/terms',
        '/privacy-policy',
        '/refund-and-cancellation',
        '/payment/success',
        '/payment/cancel',
      ];
      if (!excludedRoutes.includes(location.pathname)) {
        window.location.href = 'https://orcasvalley.web.app/'; // Use window.location.href for absolute URL redirection
      }
    };

    checkTokenExpiry();

    const interval = setInterval(checkTokenExpiry, 60 * 1000);
    return () => clearInterval(interval);
  }, [location]);

  return null;
};

const App: React.FC = () => {
  useEffect(() => {
    // ✅ Google Tag Manager Script
    const gtagScript = document.createElement("script");
    gtagScript.async = true;
    gtagScript.src = "https://www.googletagmanager.com/gtag/js?id=AW-667504395";
    document.head.appendChild(gtagScript);

    gtagScript.onload = () => {
      window.dataLayer = window.dataLayer || [];
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };
      window.gtag("js", new Date());
      window.gtag("config", "AW-667504395");
    };

    // ✅ Restore UTM & Referral Tracking Logic
    const currentUrl = window.location.href;
    const normalizedUrl = currentUrl.endsWith("/")
      ? currentUrl.slice(0, -1)
      : currentUrl;

    console.log("Normalized URL:", normalizedUrl);

    const storedUrl = localStorage.getItem("sign_up_link");
    if (!storedUrl || storedUrl !== normalizedUrl) {
      localStorage.setItem("sign_up_link", normalizedUrl);
    }

    // Parse UTM parameters
    const urlParams = new URLSearchParams(window.location.search);
    const utmParams = urlParams.toString();
    console.log("Extracted UTM Parameters:", utmParams);

    if (utmParams) {
      localStorage.setItem("utm_params", utmParams);
    }

    // Store referral details in sessionStorage
    const referralCode = urlParams.get("referralCode");
    const referredByOrgId = urlParams.get("orgId");
    const referredByUserId = urlParams.get("userId");

    if (referredByOrgId) sessionStorage.setItem("referredByOrgId", referredByOrgId);
    if (referredByUserId) sessionStorage.setItem("referredByUserId", referredByUserId);
    if (referralCode) sessionStorage.setItem("referralCode", referralCode);

    console.log("Referral Data Stored:", { referredByOrgId, referredByUserId, referralCode });
  }, []);

  return (
    <HelmetProvider>
      <SocketProvider>
        <OutlineProvider> {/* ✅ Wrap App with Context Provider */}
          <Router>
            <AuthCheckComponent />
            <TrackPageView />
            <Routes>
            {/* Public Routes */}
       {/* Public Routes */}
       {/* <Route path="/" element={<LandingPage />} /> */}
          {/* <Route path="/white-label" element={<NewLandingPage />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/contact-us" element={<ContactUsPage />} />
          <Route path="/use-cases-sales" element={<UseCasesSalesPage />} />
          <Route path="/use-cases-product" element={<UseCasesProductPage />} />
          <Route path="/use-cases-pitch" element={<UseCasesPitchPage />} />
          <Route path="/use-cases-marketing" element={<UseCasesMarketingPage />} />
          <Route path="/use-cases-employee" element={<UseCasesEmployeePage />} />
          <Route path="/use-cases-project" element={<UseCasesProjectPage />} />
          <Route path="/use-cases-board" element={<UseCasesBoardPage />} />
          <Route path="/use-cases-education" element={<UseCasesEducationPage />} /> */}

      <Route path="/" element={<Homepage />} />
      <Route path="/about" element={<AboutUs />} />
      <Route path="/auth" element={<Login />} />
      <Route path="/startup" element={<Startup />} />
      <Route path="/startup-accelerator" element={<Startup />} />
      <Route path="/startup-studio" element={<StartupStudio />} />
      <Route path="/contact-us" element={<ContactUs />} />
      <Route path="/terms" element={<TermsAndConditions />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/refund-and-cancellation" element={<RefundAndCancellation />} />"
          <Route path="/blog1" element={<Blog1/>}/>
              <Route path="/blog2" element={<Blog2/>}/>
              <Route path="/blog3" element={<Blog3/>}/>
              <Route path="/blog4" element={<Blog4/>}/>
              <Route path="/blog5" element={<Blog5/>}/>
              <Route path="/blog6" element={<Blog6/>}/>
              <Route path="/blog7" element={<Blog7/>}/>
              <Route path="/blog8" element={<Blog8/>}/>
              <Route path="/blog9" element={<Blog9/>}/>
              <Route path="/blog10" element={<Blog10/>}/>

            {/* Sharing Routes */}
            <Route path="/presentation-share" element={<PresentationShare />} />
            <Route path="/share" element={<PitchDeckShare />} />

            {/* Payment Routes */}
            <Route path="/payment/success" element={<MontyPaySuccessPage />} />
            <Route path="/payment/cancel" element={<MontyPayCancelPage />} />

            {/* Protected Routes */}
            <Route element={<ProtectedRoutes />}>
              <Route path="/onboarding" element={<OnboardingPage />} />
              <Route path="/organization-profile" element={<ViewOrganizationProfilePage />} />
              <Route path="/edit-organization-profile" element={<EditOrganizationProfilePage />} />
              <Route path="/new-presentation" element={<PresentationTypePage />} />
              <Route path="/presentation-view" element={<PresentationViewPage />} />
              <Route path="/history" element={<HistoryPage />} />
              <Route path="/refer" element={<ReferPage />} />
              <Route path='/investor-dashboard' element={<InvestorDashboard/>}/>
            </Route>
          </Routes>
        </Router>
        <ToastContainer />
      </OutlineProvider>
    </SocketProvider>
    </HelmetProvider>
  );
};

export default App;